import { useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import { Button, Input, VirtualizedCombobox } from '@roger/ui-components';
import { useValueCodeOptions } from '../../../hooks/useValueCodeOptions';
import {
  formatDollarAmount,
  isValidDollarInput,
} from '../../../utils/currencyUtils';
import type { ValueRow } from './types';

function CodeCell({
  code,
  onCodeChange,
}: {
  code: string;
  onCodeChange: (code: string) => void;
}) {
  const { options, loading } = useValueCodeOptions();

  const comboOptions = useMemo(() => {
    const base = options.map(o => ({
      value: o.code,
      label: `${o.code} — ${o.description}`,
    }));
    if (code && !options.some(o => o.code === code)) {
      base.unshift({ value: code, label: code });
    }
    return base;
  }, [options, code]);

  if (!loading && options.length === 0) {
    return (
      <Input
        inputSize='sm'
        className='w-28 font-mono'
        placeholder='Code'
        value={code}
        onChange={e => onCodeChange(e.target.value)}
      />
    );
  }

  return (
    <VirtualizedCombobox
      options={comboOptions}
      value={code}
      triggerLabel={code || undefined}
      onValueChange={next => {
        if (next === '') return;
        onCodeChange(next);
      }}
      placeholder='Code'
      searchPlaceholder='Search value codes...'
      isLoading={loading}
      size='sm'
      className='w-28'
      popoverWidth='420px'
    />
  );
}

export function ValueCodesField({
  rows,
  onChange,
}: {
  rows: ValueRow[];
  onChange: (next: ValueRow[]) => void;
}) {
  const update = (i: number, patch: Partial<ValueRow>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { code: '', amount: '' }]);

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center gap-2 text-xs font-medium text-muted-foreground'>
        <span className='w-28'>Value Code</span>
        <span className='w-28'>Amount</span>
      </div>
      {rows.map((entry, index) => (
        <div key={index} className='flex items-center gap-2'>
          <CodeCell
            code={entry.code}
            onCodeChange={code => update(index, { code })}
          />
          <Input
            inputSize='sm'
            type='text'
            inputMode='decimal'
            className='w-28'
            placeholder='0.00'
            value={entry.amount}
            onChange={e => {
              const raw = e.target.value;
              if (!isValidDollarInput(raw)) return;
              update(index, { amount: raw });
            }}
            onBlur={() => {
              const amount = formatDollarAmount(entry.amount);
              if (amount !== entry.amount) update(index, { amount });
            }}
          />
          <Button
            type='button'
            variant='ghost'
            size='icon'
            aria-label='Remove value code'
            onClick={() => remove(index)}
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      ))}
      <Button
        type='button'
        variant='outline'
        size='sm'
        className='self-start'
        onClick={add}
      >
        <Plus className='mr-1 h-3 w-3' />
        Add Value Code
      </Button>
    </div>
  );
}
